import { z } from "zod";
import { fileKindOf, fileRefSchema, type FileRef } from "../../lib/files";
import { compareText, fail, ok, type FieldKind } from "../kind";

// La forme d'un fichier (`FileRef`) et sa nature (`fileKindOf`) vivent dans
// `lib/files`, partagées avec le node `file` : un champ fichier en porte une
// liste, le node un seul.
export type { FileRef };

export const FILE_CATEGORIES = ["image", "pdf", "audio", "video", "other"] as const;
export type FileCategory = (typeof FILE_CATEGORIES)[number];

export const fileSettings = z.object({
  // Vide : tout est accepté.
  accept: z.array(z.enum(FILE_CATEGORIES)).default([]),
  multiple: z.boolean().default(true),
  maxSizeMb: z.number().positive().max(500).optional(),
  display: z.enum(["thumbnails", "list", "cover"]).default("thumbnails"),
});

/** La catégorie d'un champ pour un type MIME : sa nature, ou « autre ». */
export function fileCategory(mime: string): FileCategory {
  const kind = fileKindOf({ mime });
  return (FILE_CATEGORIES as readonly string[]).includes(kind) ? (kind as FileCategory) : "other";
}

/**
 * Le refus qui tient aux réglages (type, taille), ou null. Partagé par
 * `coerce`, la vérification avant l'envoi (src/fields/shared/fileRefusal.ts)
 * et la recopie d'une conversion (lib/fieldExtraction.ts).
 */
export function fileRefusal(
  settings: { accept: readonly FileCategory[]; maxSizeMb?: number },
  file: Pick<FileRef, "name" | "mime" | "size">,
): string | null {
  if (settings.accept.length > 0 && !settings.accept.includes(fileCategory(file.mime))) {
    return `"${file.name}" is not an accepted file type.`;
  }
  if (settings.maxSizeMb !== undefined && file.size > settings.maxSizeMb * 1024 * 1024) {
    return `"${file.name}" is larger than ${settings.maxSizeMb} MB.`;
  }
  return null;
}

const MAX_FILES = 100;

export const fileKind: FieldKind<"file"> = {
  type: "file",
  settings: fileSettings,
  value: z.array(fileRefSchema).min(1).max(MAX_FILES),
  coerce(input, settings) {
    const items = Array.isArray(input) ? input : [input];
    const files: FileRef[] = [];
    for (const item of items) {
      if (typeof item === "string") {
        return fail("Files must be uploaded first; a URL or a name is not a file.");
      }
      const parsed = fileRefSchema.safeParse(item);
      if (!parsed.success) return fail("Invalid file reference.");
      const file = parsed.data;
      const refusal = fileRefusal(settings, file);
      if (refusal) return fail(refusal);
      if (!files.some((existing) => existing.key === file.key)) files.push(file);
    }
    if (files.length === 0) return ok(null);
    if (!settings.multiple && files.length > 1) return fail("Only one file is accepted.");
    if (files.length > MAX_FILES) return fail(`At most ${MAX_FILES} files.`);
    return ok(files);
  },
  isEmpty: (value) => value.length === 0,
  format: (value) => value.map((file) => file.name).join(", "),
  compare: (a, b) => compareText(a[0]?.name ?? "", b[0]?.name ?? "") || a.length - b.length,
  filters: [],
  aggregates: ["count", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty"],
};
